'use memo';

import { useState, useRef, useEffect, useContext } from 'react';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import {
  AgxRow,
  AgxColumn,
  Images,
  AgxBodyText,
  AgxCaption,
  AgxColours,
  AgxSlideUpModal,
  AgxButton,
  AgxDivider,
  AgxTextInput,
  AgxLabel,
  AgxCountdown,
  AgxEdit2Outline,
  EnvelopeSubmissionType,
} from '@urbanx/agx-ui-components';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { AuthContext } from '@urbanx/agx.auth.client';
import GreenCheck from '../../Assets/checkmark-circle-2.svg?react';
import useOutsideAlerter from 'hooks/useOutsideAlerter';
import { DigitalSignatureClientDetails } from 'Api/Campaigns/Types/types';
import { setAndShowSuccessToast, setAndShowErrorToast } from 'store/config';
import { resendEnvelope } from 'Api/DocuSign/docusignApi';
import { sendForm } from 'Api/Campaigns/campaignsApi';
import { Breakpoints, ScreenSize } from 'utils/screen';
import { useSelectedCampaign } from 'hooks/useSelectedCampaign';
import { useSelectedContract } from 'hooks/useSelectedContract';
import './Signatory.scss';
import {
  EnvelopeSubmissionRecipient,
  EnvelopeSubmissionRecipientStatus,
  FormType,
} from '@urbanx/agx.campaigns.client';

dayjs.extend(advancedFormat);

interface SignatoryProps {
  signatory: EnvelopeSubmissionRecipient;
  envelopeSubmissionType?: EnvelopeSubmissionType | null;
  envelopeId: string;
  declinedStatus: boolean;
}

export const Signatory = ({
  signatory,
  envelopeSubmissionType,
  envelopeId,
  declinedStatus,
}: SignatoryProps) => {
  const { getAuthToken } = useContext(AuthContext);
  const {
    name,
    emailAddress,
    status,
    sentDateTime,
    deliveredDateTime: viewedAt,
    signedDateTime: signedAt,
  } = signatory;

  const recipientId = signatory.recipientId ?? '';

  const dispatch = useAppDispatch();
  const { selectedCampaign, isSelectedCampaignPending } = useSelectedCampaign();
  const selectedContract = useSelectedContract();
  const [isDisabled, setIsDisabled] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0); // Remaining time in seconds
  const [isActive, setIsActive] = useState(false);
  const [sending, setSending] = useState(false);
  const [showResendEmailModal, setShowResendEmailModal] = useState(false);
  const isMobile = ScreenSize() === Breakpoints.Mobile;
  const [recipientDetails, setRecipientDetails] = useState<DigitalSignatureClientDetails>({
    emailOverrides: null,
    name: signatory.name,
    preferredName: signatory.preferredName,
    stakeholderType: signatory.stakeholderType,
  });

  const sentAtDayJs = dayjs(sentDateTime);
  const viewedAtDayJs = dayjs(viewedAt);
  const signedAtDayJs = dayjs(signedAt);

  const moreOptionsRef = useRef(null);
  useOutsideAlerter(moreOptionsRef, (onTriggerOutside) => {
    if (onTriggerOutside === 'outside') setIsActive(false);
  });

  const submissionStatusToIcon = (submissionStatus: EnvelopeSubmissionRecipientStatus) => {
    switch (submissionStatus) {
      case EnvelopeSubmissionRecipientStatus.Created:
        return envelopeSubmissionType === EnvelopeSubmissionType.DocuSign ? (
          <Images.PaperPlaneOutline />
        ) : (
          <Images.Clock />
        );
      case EnvelopeSubmissionRecipientStatus.Sent:
        return envelopeSubmissionType === EnvelopeSubmissionType.DocuSign ? (
          <Images.Clock />
        ) : (
          <GreenCheck />
        );
      case EnvelopeSubmissionRecipientStatus.Opened:
        return <Images.Clock />;
      case EnvelopeSubmissionRecipientStatus.Signed:
      case EnvelopeSubmissionRecipientStatus.Complete:
        return <GreenCheck />;
      case EnvelopeSubmissionRecipientStatus.Declined:
        return <Images.RedMinusCircle />;
      case EnvelopeSubmissionRecipientStatus.Bounced:
        return <Images.WarningTriangle />;
      default:
        return <Images.PaperPlaneOutline />;
    }
  };

  const submissionStatusToFriendlyString = (
    submissionStatus: EnvelopeSubmissionRecipientStatus
  ) => {
    switch (submissionStatus) {
      case EnvelopeSubmissionRecipientStatus.Created:
        return `Sending ${envelopeSubmissionType === EnvelopeSubmissionType.DocuSign ? 'Docusign' : 'email'}...`;
      case EnvelopeSubmissionRecipientStatus.Sent:
        return envelopeSubmissionType === EnvelopeSubmissionType.DocuSign
          ? 'Needs to Sign'
          : `Sent at ${sentAtDayJs.format('hh:mm A')} on ${sentAtDayJs.format('dddd, MMMM Do YYYY')}`;
      case EnvelopeSubmissionRecipientStatus.Opened:
        return `Viewed at ${viewedAtDayJs.format(
          'hh:mm A'
        )} on ${viewedAtDayJs.format('dddd, MMMM Do YYYY')}`;
      case EnvelopeSubmissionRecipientStatus.Signed:
      case EnvelopeSubmissionRecipientStatus.Complete:
        return `${envelopeSubmissionType === EnvelopeSubmissionType.DocuSign ? 'Signed' : 'Received'} at ${(envelopeSubmissionType ===
        EnvelopeSubmissionType.DocuSign
          ? signedAtDayJs
          : sentAtDayJs
        ).format('hh:mm A')} on ${(envelopeSubmissionType === EnvelopeSubmissionType.DocuSign
          ? signedAtDayJs
          : sentAtDayJs
        ).format('dddd, MMMM Do YYYY')}`;
      case EnvelopeSubmissionRecipientStatus.Declined:
        return 'Declined';
      case EnvelopeSubmissionRecipientStatus.Bounced:
        return 'Email Bounced';
      default:
        return '';
    }
  };

  const handleResend = async () => {
    if (envelopeSubmissionType === EnvelopeSubmissionType.Email) {
      setShowResendEmailModal(true);
      return;
    }

    const authToken = await getAuthToken();

    if (!authToken) return;

    setIsActive(false);
    setSending(true);

    try {
      const successMessage = 'Envelope resent successfully!';
      const data = await resendEnvelope(authToken, envelopeId, recipientId ?? '');
      if (data) {
        dispatch(setAndShowSuccessToast(successMessage));
        setSending(false);
      }
    } catch (err: any) {
      console.error(err);
      setSending(false);
      dispatch(setAndShowErrorToast(err.message));
    }
  };

  const sendEmail = async () => {
    if (!getAuthToken || !selectedCampaign?.campaignId) return;

    try {
      const authToken = await getAuthToken();

      if (!authToken) return;

      await sendForm(authToken, {
        campaignId: selectedCampaign?.campaignId,
        formId: selectedContract?.inProgressForm?.formId || '',
        formType: selectedContract?.inProgressForm?.formType as FormType,
        state: selectedCampaign.state,
        digitalSignatureSubmission: {
          recipientDetails: recipientDetails,
          sendVendorsNow: false,
          resendEnvelope: true,
        },
      });
      setShowResendEmailModal(false);
      setIsDisabled(true);
      setTimeLeft(5 * 60);
    } catch (err: any) {
      console.error(err);
    }
  };

  const handleCountdownComplete = () => {
    setIsDisabled(false);
  };

  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    if (isDisabled && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    } else if (timeLeft === 0 && isDisabled) {
      handleCountdownComplete();
    }

    return () => clearInterval(timer);
  }, [isDisabled, timeLeft]);

  const resendEmailDialogBody = (
    <AgxColumn veryLargeGap extraClasses={'resendModalStyle'}>
      <AgxEdit2Outline width={35} height={35} />
      <AgxLabel large>Resend Sales Advice</AgxLabel>
      <AgxColumn fill largeGap>
        <AgxBodyText small>
          Confirm email for <AgxBodyText extraClasses="strong">{name}</AgxBodyText>
        </AgxBodyText>
        <AgxTextInput
          id="recipientEmail"
          label="Email address"
          email
          noOptionalLabel
          onInputValueChange={({ value }) => {
            setRecipientDetails({
              ...recipientDetails,
              emailOverrides: {
                [recipientId]: value,
              },
            });
          }}
          stretch
        />
      </AgxColumn>
      <AgxDivider expanded />
      <AgxColumn fill largeGap>
        <AgxButton
          primary
          large
          text="Resend"
          dataTestId="agx-docuSignSendForDocuSigning"
          onClick={sendEmail}
        />
      </AgxColumn>
    </AgxColumn>
  );

  if (isSelectedCampaignPending) return;

  return (
    <>
      <div className="signatory">
        <div className="signatoryIconWrapper">
          {submissionStatusToIcon(status ?? EnvelopeSubmissionRecipientStatus.Sent)}
        </div>
        <AgxColumn extraClasses="signatoryContentWrapper">
          <AgxBodyText medium extraClasses="signatoryName">
            {name}
          </AgxBodyText>
          <AgxCaption colour={AgxColours.NEUTRAL_GREY_800} extraClasses="signatoryDetails">
            {emailAddress}
          </AgxCaption>
          <AgxCaption colour={AgxColours.NEUTRAL_GREY_800} extraClasses="signatoryDetails">
            {submissionStatusToFriendlyString(status ?? EnvelopeSubmissionRecipientStatus.Sent)}
          </AgxCaption>
        </AgxColumn>
        {((!signedAt &&
          sentDateTime &&
          envelopeSubmissionType === EnvelopeSubmissionType.DocuSign &&
          (status === EnvelopeSubmissionRecipientStatus.Sent ||
            status === EnvelopeSubmissionRecipientStatus.Opened) &&
          !declinedStatus) ||
          envelopeSubmissionType === EnvelopeSubmissionType.Email) && (
          <AgxRow end onClick={(e) => setIsActive(!isActive)} extraClasses="moreOptionsButton">
            <Images.More />
          </AgxRow>
        )}
        {isActive &&
          ((!signedAt &&
            sentDateTime &&
            (status === EnvelopeSubmissionRecipientStatus.Sent ||
              status === EnvelopeSubmissionRecipientStatus.Opened) &&
            !declinedStatus) ||
            envelopeSubmissionType === EnvelopeSubmissionType.Email) && (
            <div
              className={`moreOptionsContent ${sending ? 'disabled-div' : ''}`}
              ref={moreOptionsRef}
            >
              {isDisabled && status !== EnvelopeSubmissionRecipientStatus.Bounced ? (
                <AgxCountdown timeLeft={timeLeft} text="Resend in " />
              ) : (
                <div onClick={handleResend}>{sending ? 'Sending...' : 'Resend'}</div>
              )}
            </div>
          )}
      </div>
      {showResendEmailModal && (
        <AgxSlideUpModal
          closeButton
          content={resendEmailDialogBody}
          desktop={!isMobile}
          onClose={() => setShowResendEmailModal(false)}
        />
      )}
    </>
  );
};
